/**
 * Canvas.ts
 *
 * Handles the rendering and updating of the design's visual representation. It creates
 * component elements from templates and updates them when properties change.
 * The actual Canvas implementation is pluggable, e.g. dashboards use GridStack.
 */
namespace appn.designerv2 {
    export let canvas: CanvasBase;
    
    export abstract class CanvasBase {
        protected constructor() { }

        init(readOnly: boolean) {
            if (readOnly)
                return;

            eventBus.subscribeToPropertyUpdates(payload => {
                const componentId = payload.componentId;
                const component = designer.getComponent(componentId);
                if (component) {
                    const element = $(`#${componentId}`);
                    if (element.length)
                        this.updateComponentElement(element, component);
                }
            });
        }
        
        abstract render(design: IDesign);
        
        abstract bindDragIn();

        createComponentElement(component: IComponent): JQuery {
            let $template = appn.utils.cloneTemplate(`component-${component.type}`, false);
            if ($template.length === 0)
                $template = appn.utils.cloneTemplate('component-widget');
            
            $template.attr('id', component.id);

            this.onCreateComponentElement(component, $template);

            this.updateComponentElement($template, component);

            return $template;
        }
        
        abstract onCreateComponentElement(component, $template);

        updateComponentElement(element: JQuery, component: IComponent): void {
            if (component.title)
                element.find('.componentTitle').text(component.title?.eng);

            const header = element.find('.componentLabel');
            if (component.label)
                header.text(component.label?.eng);

            const hint = element.find('.componentHint');
            if (component.hint)
                hint.text(component.hint?.eng);

            if (component.labelStyle)
                this.applyFontStyle(header, component.labelStyle);

            if (component.hintStyle)
                this.applyFontStyle(hint, component.hintStyle);

            if (component.bgColor)
                element.find('.card').css('background-color', component.bgColor);

            const def = componentManager.getDefinition(component.type);
            def?.render?.(element, component);
            
            this.onUpdateComponentElement(element, component);

            // Ensure validation error class is maintained if present
            const hasPropertyError = $('.has-error[id^="prop-' + component.id + '-"]').length > 0;
            if (hasPropertyError) {
                element.addClass('has-validation-error');
            }
        }

        protected applyFontStyle(element: JQuery, style: IFontStyle) {
            if (!element || element.length === 0)
                return;

            element.css('color', style.color || '');
            
            const sizeMap: { [key: string]: string } = {
                'Small': '0.6rem',    // 0.8rem * 0.75 = 0.6rem
                'Medium': '0.75rem',  // 1rem * 0.75 = 0.75rem
                'Large': '0.9rem'     // 1.2rem * 0.75 = 0.9rem
            };
            
            element.css('font-size', sizeMap[style.size || 'Medium']);
            element.css('font-weight', style.decor === 'Bold' ? 'bold' : 'normal');
            element.css('font-style', style.decor === 'Italic' ? 'italic' : 'normal');
        }
        
        abstract onUpdateComponentElement(element: JQuery, component: IComponent);
        
        abstract removeComponent(componentId: string | undefined);
    }
}
